import { gapi } from 'gapi-script';

class GoogleDriveService {
  private static instance: GoogleDriveService;
  private accessToken: string | null = null;
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;
  private tokenClient: any = null;

  private constructor() {}

  public static getInstance(): GoogleDriveService {
    if (!GoogleDriveService.instance) {
      GoogleDriveService.instance = new GoogleDriveService();
    }
    return GoogleDriveService.instance;
  }

  public async initialize(): Promise<void> {
    if (this.isInitialized && this.accessToken) {
      return;
    }

    // 如果已經在初始化中，返回現有的 Promise
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = new Promise<void>(async (resolve, reject) => {
      try {
        // 載入 Google Identity Services script
        await new Promise<void>((loadResolve, loadReject) => {
          const script = document.createElement('script');
          script.src = 'https://accounts.google.com/gsi/client';
          script.async = true;
          script.defer = true;
          script.onload = () => loadResolve();
          script.onerror = () => loadReject(new Error('Failed to load Google Identity Services'));
          document.body.appendChild(script);
        });

        // 檢查是否有有效的 Client ID
        const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
        if (!clientId) {
          throw new Error('未設定 Google Client ID。請在環境變數中設定 VITE_GOOGLE_CLIENT_ID');
        }

        // 初始化 Google Identity Services
        this.tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: 'https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/drive.file',
          callback: (tokenResponse: any) => {
            if (tokenResponse.access_token) {
              this.accessToken = tokenResponse.access_token;
              console.log('獲取訪問令牌成功');
              this.isInitialized = true;
              resolve();
            } else {
              reject(new Error('無法獲取訪問令牌'));
            }
          }
        });

        // 請求訪問令牌
        this.tokenClient.requestAccessToken({ prompt: 'consent' });

      } catch (error) {
        console.error('初始化 Google Drive API 失敗:', error);
        this.isInitialized = false;
        this.accessToken = null;
        
        // 提供更友好的錯誤訊息
        if (error instanceof Error) {
          if (error.message.includes('deleted_client')) {
            reject(new Error('Google OAuth 客戶端已被刪除。請聯繫開發者更新設定。'));
          } else if (error.message.includes('invalid_client')) {
            reject(new Error('Google OAuth 客戶端設定無效。請檢查 Client ID 設定。'));
          } else {
            reject(error);
          }
        } else {
          reject(new Error('初始化 Google Drive API 時發生未知錯誤'));
        }
      } finally {
        this.initializationPromise = null;
      }
    });

    return this.initializationPromise;
  }

  private async ensureAuthorized(): Promise<void> {
    if (!this.isInitialized || !this.accessToken) {
      await this.initialize();
    }
  }

  private async fetchWithAuth(url: string, options: RequestInit = {}) {
    await this.ensureAuthorized();

    if (!this.accessToken) {
      throw new Error('未獲取訪問令牌');
    }

    const requestOptions: RequestInit = {
      ...options,
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        ...options.headers
      }
    };

    const response = await fetch(url, requestOptions);

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        // Token 可能過期，嘗試重新獲取
        console.log('Token 可能過期，嘗試重新獲取...');
        this.isInitialized = false;
        this.accessToken = null;
        await this.initialize();
        // 重試請求
        return await fetch(url, requestOptions);
      }

      // 處理其他錯誤
      const errorData = await response.json().catch(() => ({}));
      const error: any = new Error(errorData.error?.message || 'API request failed');
      error.status = response.status;
      error.details = errorData;
      throw error;
    }

    return response;
  }

  public async getVideoMetadata(fileId: string) {
    try {
      const metadataUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?fields=id,name,mimeType,size,capabilities,thumbnailLink`;
      const response = await this.fetchWithAuth(metadataUrl);
      const data = await response.json();
      console.log('Video metadata:', data);
      return data;
    } catch (error: any) {
      console.error('Error fetching video metadata:', error);
      if (error.status === 403 || error.status === 401) {
        throw new Error('無法存取此影片。請確認影片權限設定為「任何人都可以查看」。');
      } else if (error.status === 404) {
        throw new Error('找不到影片。請確認連結是否正確。');
      }
      throw new Error(error.message || '獲取影片資訊失敗');
    }
  }

  public async getVideoStreamUrl(fileId: string): Promise<string> {
    await this.ensureAuthorized();
    // 回傳 Proxy API 的網址，避免 CORS 問題
    // 使用相對路徑，讓 Firebase Hosting rewrite 規則處理
    const apiBaseUrl = import.meta.env.DEV ? 'http://localhost:3001' : '';
    return `${apiBaseUrl}/api/google-drive/${fileId}?access_token=${this.accessToken}`;
  }

  public getPreviewUrl(fileId: string): string {
    if (!fileId) {
      throw new Error('無效的檔案 ID');
    }
    return `https://drive.google.com/file/d/${fileId}/preview`;
  }

  public extractFileId(url: string): string {
    if (!url) {
      throw new Error('請輸入 Google Drive 連結');
    }

    // Remove @ symbol if present
    const cleanUrl = url.startsWith('@') ? url.substring(1) : url;
    
    // Extract file ID
    let fileId = '';
    
    // 處理不同格式的 Google Drive 連結
    if (cleanUrl.includes('/file/d/')) {
      const match = cleanUrl.match(/\/file\/d\/([^/]+)/);
      if (match) fileId = match[1];
    } else if (cleanUrl.includes('id=')) {
      const match = cleanUrl.match(/id=([^&]+)/);
      if (match) fileId = match[1];
    } else if (cleanUrl.includes('drive.google.com/open')) {
      const match = cleanUrl.match(/id=([^&]+)/);
      if (match) fileId = match[1];
    }
    
    if (!fileId) {
      throw new Error('無效的 Google Drive 連結格式');
    }
    
    return fileId;
  }

  // 建立 CheckPlay 專用資料夾
  public async createCheckPlayFolder(): Promise<string> {
    await this.ensureAuthorized();
    
    if (!this.accessToken) {
      throw new Error('未獲取訪問令牌');
    }

    const folderMetadata = {
      name: 'CheckPlay Videos',
      mimeType: 'application/vnd.google-apps.folder',
      description: 'CheckPlay 應用程式的影片儲存資料夾'
    };

    try {
      const response = await this.fetchWithAuth('https://www.googleapis.com/drive/v3/files', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(folderMetadata)
      });

      const folder = await response.json();
      console.log('CheckPlay 資料夾建立成功:', folder);
      return folder.id;
    } catch (error) {
      console.error('建立 CheckPlay 資料夾失敗:', error);
      throw error;
    }
  }

  // 上傳影片到 Google Drive
  public async uploadVideo(file: File): Promise<string> {
    await this.ensureAuthorized();
    
    if (!this.accessToken) {
      throw new Error('未獲取訪問令牌');
    }

    try {
      // 1. 確保 CheckPlay 資料夾存在
      let folderId = await this.getCheckPlayFolderId();
      if (!folderId) {
        folderId = await this.createCheckPlayFolder();
      }

      // 2. 準備檔案元資料
      const fileMetadata = {
        name: file.name,
        mimeType: file.type,
        parents: [folderId],
        description: `上傳時間: ${new Date().toISOString()}`
      };

      // 3. 建立 FormData 進行上傳
      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(fileMetadata)], {
        type: 'application/json'
      }));
      form.append('file', file);

      // 4. 上傳檔案
      const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        },
        body: form
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`上傳失敗: ${errorData.error?.message || '未知錯誤'}`);
      }

      const uploadedFile = await response.json();
      console.log('影片上傳成功:', uploadedFile);
      
      // 上傳完成後，自動設定檔案為公開權限
      try {
        await this.setFilePublic(uploadedFile.id);
        console.log('檔案權限設定完成');
      } catch (permissionError) {
        console.warn('設定檔案權限失敗，但不影響上傳:', permissionError);
      }
      
      return uploadedFile.id;
    } catch (error) {
      console.error('上傳影片失敗:', error);
      throw error;
    }
  }

  // 取得 CheckPlay 資料夾 ID
  private async getCheckPlayFolderId(): Promise<string | null> {
    try {
      const response = await this.fetchWithAuth(
        'https://www.googleapis.com/drive/v3/files?q=name="CheckPlay Videos" and mimeType="application/vnd.google-apps.folder" and trashed=false'
      );

      const data = await response.json();
      if (data.files && data.files.length > 0) {
        return data.files[0].id;
      }

      return null;
    } catch (error) {
      console.error('取得資料夾 ID 失敗:', error);
      return null;
    }
  }

  // 取得使用者的影片列表
  public async getUserVideos(): Promise<any[]> {
    await this.ensureAuthorized();
    
    if (!this.accessToken) {
      throw new Error('未獲取訪問令牌');
    }

    try {
      const folderId = await this.getCheckPlayFolderId();
      if (!folderId) {
        return [];
      }

      const response = await this.fetchWithAuth(
        `https://www.googleapis.com/drive/v3/files?q="${folderId}" in parents and trashed=false&orderBy=createdTime desc&fields=files(id,name,mimeType,size,createdTime,webViewLink,webContentLink,permissions)`
      );

      const data = await response.json();
      return data.files || [];
    } catch (error) {
      console.error('取得影片列表失敗:', error);
      throw error;
    }
  }

  // 刪除檔案
  public async deleteFile(fileId: string): Promise<void> {
    await this.ensureAuthorized();
    
    if (!this.accessToken) {
      throw new Error('未獲取訪問令牌');
    }

    try {
      const response = await this.fetchWithAuth(
        `https://www.googleapis.com/drive/v3/files/${fileId}`,
        { method: 'DELETE' }
      );

      console.log('檔案刪除成功:', fileId);
    } catch (error) {
      console.error('刪除檔案失敗:', error);
      throw error;
    }
  }

  // 設定檔案權限為公開
  public async setFilePublic(fileId: string): Promise<void> {
    await this.ensureAuthorized();
    
    if (!this.accessToken) {
      throw new Error('未獲取訪問令牌');
    }

    try {
      // 1. 檢查是否已有公開權限
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}/permissions`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('檢查檔案權限失敗');
      }

      const permissions = await response.json();
      const hasPublicPermission = permissions.permissions?.some((p: any) => 
        p.role === 'reader' && p.type === 'anyone'
      );

      if (hasPublicPermission) {
        console.log('檔案已有公開權限');
        return;
      }

      // 2. 設定公開權限
      const permissionData = {
        role: 'reader',
        type: 'anyone'
      };

      const setPermissionResponse = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}/permissions`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(permissionData)
        }
      );

      if (!setPermissionResponse.ok) {
        const errorData = await setPermissionResponse.json();
        throw new Error(`設定權限失敗: ${errorData.error?.message || '未知錯誤'}`);
      }

      console.log('檔案權限設定為公開成功:', fileId);

      // 3. 強制更新檔案權限（確保權限生效）
      await this.forceUpdateFilePermissions(fileId);
    } catch (error) {
      console.error('設定檔案權限失敗:', error);
      throw error;
    }
  }

  // 強制更新檔案權限
  private async forceUpdateFilePermissions(fileId: string): Promise<void> {
    try {
      // 1. 先刪除所有現有權限
      const permissionsResponse = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}/permissions`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          }
        }
      );

      if (permissionsResponse.ok) {
        const permissions = await permissionsResponse.json();
        for (const permission of permissions.permissions || []) {
          if (permission.type === 'anyone') {
            // 刪除現有的公開權限
            await fetch(
              `https://www.googleapis.com/drive/v3/files/${fileId}/permissions/${permission.id}`,
              {
                method: 'DELETE',
                headers: {
                  'Authorization': `Bearer ${this.accessToken}`
                }
              }
            );
          }
        }
      }

      // 2. 重新設定公開權限
      const permissionData = {
        role: 'reader',
        type: 'anyone'
      };

      const setPermissionResponse = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}/permissions`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(permissionData)
        }
      );

      if (!setPermissionResponse.ok) {
        console.warn('重新設定檔案權限失敗');
        return;
      }

      // 3. 使用 PATCH 方法強制更新檔案屬性
      const updateData = {
        copyRequiresWriterPermission: false,
        viewersCanCopyContent: true
      };

      const patchResponse = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        }
      );

      if (!patchResponse.ok) {
        console.warn('強制更新檔案屬性失敗，但不影響基本權限設定');
        return;
      }

      console.log('檔案權限強制更新成功:', fileId);
    } catch (error) {
      console.warn('強制更新檔案權限時發生錯誤，但不影響基本權限設定:', error);
    }
  }

  // 檢查檔案上傳是否完整
  public async checkFileUpload(fileId: string): Promise<boolean> {
    await this.ensureAuthorized();
    
    if (!this.accessToken) {
      throw new Error('未獲取訪問令牌');
    }

    try {
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}?fields=id,name,size,mimeType,permissions,shared`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          }
        }
      );

      if (!response.ok) {
        return false;
      }

      const file = await response.json();
      
      // 檢查檔案大小是否大於 0
      if (file.size === '0' || parseInt(file.size) === 0) {
        console.warn('檔案大小為 0，可能上傳不完整:', fileId);
        return false;
      }

      // 檢查檔案類型是否為影片
      const isVideo = file.mimeType?.startsWith('video/');
      if (!isVideo) {
        console.warn('檔案不是影片格式:', file.mimeType);
        return false;
      }

      // 檢查檔案權限
      const hasPublicPermission = file.permissions?.some((p: any) => 
        p.role === 'reader' && p.type === 'anyone'
      );
      
      if (!hasPublicPermission) {
        console.warn('檔案沒有公開權限，嘗試自動設定:', fileId);
        try {
          await this.setFilePublic(fileId);
          console.log('檔案權限自動設定成功');
        } catch (permissionError) {
          console.error('自動設定權限失敗:', permissionError);
        }
      }

      console.log('檔案檢查通過:', file);
      return true;
    } catch (error) {
      console.error('檢查檔案失敗:', error);
      return false;
    }
  }

  // 手動修復檔案權限
  public async fixFilePermissions(fileId: string): Promise<boolean> {
    try {
      console.log('開始修復檔案權限:', fileId);
      
      // 1. 設定基本公開權限
      await this.setFilePublic(fileId);
      
      // 2. 強制更新檔案權限
      await this.forceUpdateFilePermissions(fileId);
      
      // 3. 使用更強制的方法設定權限
      await this.forceSetPublicPermission(fileId);
      
      // 4. 強制更新檔案分享設定
      await this.forceUpdateFileSharing(fileId);
      
      // 5. 等待權限生效
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // 6. 驗證權限是否生效
      const isFixed = await this.verifyFileAccess(fileId);
      
      if (isFixed) {
        console.log('檔案權限修復成功:', fileId);
        return true;
      } else {
        console.warn('檔案權限修復失敗，可能需要手動設定:', fileId);
        return false;
      }
    } catch (error) {
      console.error('修復檔案權限失敗:', error);
      return false;
    }
  }

  // 強制設定公開權限（使用多種方法）
  private async forceSetPublicPermission(fileId: string): Promise<void> {
    try {
      console.log('使用強制方法設定公開權限:', fileId);
      
      // 方法 1: 使用 permissions 端點設定
      const permissionData = {
        role: 'reader',
        type: 'anyone',
        allowFileDiscovery: false
      };

      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}/permissions`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(permissionData)
        }
      );

      if (!response.ok) {
        console.warn('強制設定權限失敗，嘗試其他方法');
        
        // 方法 2: 使用 files 端點更新
        const updateData = {
          copyRequiresWriterPermission: false,
          viewersCanCopyContent: true,
          writersCanShare: true
        };

        const patchResponse = await fetch(
          `https://www.googleapis.com/drive/v3/files/${fileId}`,
          {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(updateData)
          }
        );

        if (!patchResponse.ok) {
          console.warn('所有權限設定方法都失敗');
          return;
        }
      }

      // 方法 3: 使用更強制的方式設定權限
      await this.forceUpdateFileSharing(fileId);

      console.log('強制權限設定成功:', fileId);
    } catch (error) {
      console.warn('強制權限設定時發生錯誤:', error);
    }
  }

  // 強制更新檔案分享設定
  private async forceUpdateFileSharing(fileId: string): Promise<void> {
    try {
      console.log('強制更新檔案分享設定:', fileId);
      
      // 使用 files 端點強制更新分享設定
      const sharingData = {
        // 確保檔案可以被公開存取
        copyRequiresWriterPermission: false,
        viewersCanCopyContent: true,
        writersCanShare: true,
        // 強制設定為公開
        sharingUser: {
          permissionId: 'anyoneWithLink'
        }
      };

      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(sharingData)
        }
      );

      if (!response.ok) {
        console.warn('強制更新檔案分享設定失敗');
        return;
      }

      // 等待一下，然後重新設定權限
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 重新設定權限
      const permissionData = {
        role: 'reader',
        type: 'anyone',
        allowFileDiscovery: false
      };

      const permissionResponse = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}/permissions`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(permissionData)
        }
      );

      if (permissionResponse.ok) {
        console.log('檔案分享設定強制更新成功:', fileId);
      } else {
        console.warn('檔案分享設定強制更新失敗');
      }
    } catch (error) {
      console.warn('強制更新檔案分享設定時發生錯誤:', error);
    }
  }

  // 驗證檔案是否可以公開存取
  private async verifyFileAccess(fileId: string): Promise<boolean> {
    try {
      // 嘗試使用公開連結存取檔案
      const publicUrl = `https://drive.google.com/file/d/${fileId}/view?usp=sharing`;
      
      const response = await fetch(publicUrl, {
        method: 'HEAD',
        mode: 'no-cors' // 避免 CORS 問題
      });

      // 如果沒有 CORS 錯誤，表示檔案可以公開存取
      console.log('檔案權限驗證成功:', fileId);
      return true;
    } catch (error) {
      console.warn('檔案權限驗證失敗:', error);
      return false;
    }
  }
}

export const googleDriveService = GoogleDriveService.getInstance(); 